import { useEffect } from "react";
import styled from "styled-components";
import PreviewCanvas from "./PreviewCanvas";

const TRIM_DESCRIPTIONS = [
  "Core luxury trim with the essential Crown stance and hybrid character.",
  "Adds richer interior appointments and optional 21 inch wheel treatment.",
  "Top trim with the boldest presence and the most expressive finish options.",
];

export default function FlatTrimSelection({
  currentTrim,
  trims,
  onBack,
  onSelectTrim,
  onTrimChange,
}) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        onTrimChange((currentTrim + 1) % trims.length);
      }

      if (event.key === "ArrowLeft") {
        onTrimChange((currentTrim - 1 + trims.length) % trims.length);
      }

      if (event.key === "Enter") {
        onSelectTrim();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentTrim, onSelectTrim, onTrimChange, trims.length]);

  return (
    <StageRoot>
      <PreviewCanvas model={currentTrim} />

      <Overlay>
        <TopRow>
          <button type="button" className="ghost" onClick={onBack}>
            Back
          </button>
          <p>
            Flat trim picker. Use the Quest controller to click left or right,
            then confirm with Select Trim.
          </p>
        </TopRow>

        <TrimViewport>
          <Track style={{ transform: `translateX(calc(50% - ${currentTrim * 320 + 160}px))` }}>
            {trims.map((trim, index) => (
              <TrimCard
                key={trim.label}
                data-active={index === currentTrim}
                onClick={() => onTrimChange(index)}
                type="button"
              >
                <span className="index">0{index + 1}</span>
                <h2>{trim.label.toUpperCase()}</h2>
                <p>{TRIM_DESCRIPTIONS[index]}</p>
              </TrimCard>
            ))}
          </Track>
        </TrimViewport>

        <ControlsRow>
          <button type="button" className="primary" onClick={onSelectTrim}>
            Select Trim
          </button>
        </ControlsRow>
      </Overlay>
    </StageRoot>
  );
}

const StageRoot = styled.section`
  position: relative;
  height: 100vh;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 110px 32px 36px;
  pointer-events: none;

  button,
  p {
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    padding: 90px 18px 24px;
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  .ghost {
    height: 46px;
    border-radius: 999px;
    padding: 0 22px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.08);
    color: #f5f3ef;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
  }

  p {
    margin: 0;
    max-width: 420px;
    text-align: right;
    font-size: 0.92rem;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.78);
  }

  @media (max-width: 768px) {
    flex-direction: column;

    p {
      text-align: left;
      max-width: none;
    }
  }
`;

const TrimViewport = styled.div`
  overflow: hidden;
  width: 100%;
  padding: 0 16px;
`;

const Track = styled.div`
  display: flex;
  gap: 20px;
  width: max-content;
  transition: transform 320ms ease;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const TrimCard = styled.button`
  width: 300px;
  min-height: 230px;
  padding: 24px;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(10, 10, 10, 0.66);
  color: #f5f3ef;
  text-align: left;
  backdrop-filter: blur(16px);
  cursor: pointer;
  transition:
    transform 220ms ease,
    border-color 220ms ease,
    background 220ms ease;

  &[data-active="true"] {
    transform: translateY(-12px);
    border-color: rgba(255, 255, 255, 0.55);
    background: rgba(34, 34, 34, 0.86);
  }

  .index {
    display: block;
    margin-bottom: 18px;
    font-size: 0.78rem;
    letter-spacing: 0.22em;
    color: rgba(255, 255, 255, 0.5);
  }

  h2 {
    margin: 0 0 12px;
    font-size: 1.4rem;
    letter-spacing: 0.08em;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
    line-height: 1.5;
  }

  @media (max-width: 768px) {
    width: min(78vw, 280px);
    min-height: 210px;
    padding: 20px;

    h2 {
      font-size: 1.18rem;
    }
  }
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  align-items: center;

  button {
    height: 50px;
    border-radius: 999px;
    padding: 0 24px;
    border: 0;
    font-size: 0.84rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
  }

  .primary {
    background: #f5f3ef;
    color: #050505;
  }

  .ghost {
    background: rgba(255, 255, 255, 0.08);
    color: #f5f3ef;
    border: 1px solid rgba(255, 255, 255, 0.14);
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;

    button {
      width: 100%;
    }
  }
`;
